import { useMemo, useState } from 'react';
import { usePlans } from '@features/plans/model/use-plans';
import { PlanDetailDialog } from '@features/plans/ui/PlanDetailDialog';
import { PlansTable } from '@features/plans/ui/PlansTable';
import type { SubscriptionPlan } from '@features/plans/model/plan.types';
import { Spinner } from '@shared/ui/Spinner';
import { formatPrice } from '@shared/lib/format-price';
import { cn } from '@shared/lib/cn';
import type { ApiError } from '@shared/api/api-error';

type Scope = 'live' | 'all';

/**
 * The subscription plan catalogue.
 *
 * Retired plans are hidden by default but never dropped: several still carry
 * subscribers, and their revenue has to land somewhere. The toggle brings them
 * back rather than a filter, because there are only ever two useful views.
 */
export function PlansPage() {
  const { data, isPending, isFetching, isError, error } = usePlans();
  const [scope, setScope] = useState<Scope>('live');
  // The plan whose details are open; null closes the dialog.
  const [opened, setOpened] = useState<SubscriptionPlan | null>(null);

  const plans = useMemo(
    () => (scope === 'all' ? (data ?? []) : (data ?? []).filter((plan) => plan.isActive)),
    [data, scope],
  );

  const retiredCount = (data ?? []).filter((plan) => !plan.isActive).length;

  // Totals describe what is shown, so they agree with the rows under them.
  const totals = plans.reduce(
    (accumulator, plan) => ({
      active: accumulator.active + plan.activeSubscriberCount,
      revenue: accumulator.revenue + plan.monthlyRevenue,
    }),
    { active: 0, revenue: 0 },
  );

  // Every plan in this project prices in the same currency; the total only
  // carries a symbol when that actually holds.
  const currencies = new Set(plans.map((plan) => plan.currency).filter(Boolean));
  const currency = currencies.size === 1 ? [...currencies][0] : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
          <ScopeButton active={scope === 'live'} onClick={() => setScope('live')}>
            Live plans
          </ScopeButton>
          <ScopeButton active={scope === 'all'} onClick={() => setScope('all')}>
            All ({(data ?? []).length})
          </ScopeButton>
        </div>

        {scope === 'live' && retiredCount > 0 && (
          <p className="text-xs text-slate-400">
            {retiredCount} retired {retiredCount === 1 ? 'plan is' : 'plans are'} hidden.
          </p>
        )}

        <span className="flex w-4 justify-center">
          {isFetching && !isPending && <Spinner className="h-4 w-4" />}
        </span>

        {!isPending && plans.length > 0 && (
          <p className="ml-auto text-xs tabular-nums text-slate-500">
            {totals.active} active {totals.active === 1 ? 'subscriber' : 'subscribers'}
            {' · '}
            {formatPrice(Math.round(totals.revenue), currency) ?? Math.round(totals.revenue)} MRR
          </p>
        )}
      </div>

      <PlansTable
        plans={plans}
        loading={isPending}
        error={isError ? (error as ApiError).message : null}
        onOpen={setOpened}
      />

      <PlanDetailDialog plan={opened} onClose={() => setOpened(null)} />
    </div>
  );
}

function ScopeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-md px-3 py-1 text-sm font-medium transition-colors',
        active ? 'bg-periscope-600 text-white' : 'text-slate-600 hover:bg-slate-50',
      )}
    >
      {children}
    </button>
  );
}
